/**
 * SSE 流式请求工具
 * 使用 fetch + ReadableStream 实现，支持 withCredentials
 */

/**
 * 发送问题并处理 SSE 流式响应
 * @param {Object} params - 请求参数 { appId, titleId, content, fileIds }
 * @param {string} endpoint - API 端点，如 '/chat/deepSeekForApp'
 * @param {function} onChunk - 每收到一段文本时的回调 (text: string)
 * @param {function} onDone - 流结束回调
 * @param {function} onError - 错误回调 (error: Error)
 * @returns {AbortController} 可用于取消请求
 */
export function sendSSE(params, endpoint, onChunk, onDone, onError) {
  const controller = new AbortController()
  const queryString = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString()

  const url = `${endpoint}?${queryString}`

  fetch(url, {
    method: 'GET',
    credentials: 'include',
    signal: controller.signal,
    headers: {
      'Accept': 'text/event-stream',
    },
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let buffer = ''

      function read() {
        reader
          .read()
          .then(({ done, value }) => {
            if (done) {
              if (buffer.trim()) {
                const trimmed = buffer.trim()
                if (trimmed.startsWith('data:')) {
                  const data = trimmed.slice(5).trim()
                  if (data !== '[DONE]') {
                    try {
                      const parsed = JSON.parse(data)
                      const text = parsed.content || parsed.text || data
                      if (text) onChunk(text)
                    } catch {
                      if (data) onChunk(data)
                    }
                  }
                }
              }
              onDone()
              return
            }

            buffer += decoder.decode(value, { stream: true })

            const lines = buffer.split('\n')
            buffer = lines.pop() || ''

            for (const line of lines) {
              const trimmed = line.trim()
              if (!trimmed || trimmed.startsWith(':')) continue

              if (trimmed.startsWith('data:')) {
                const data = trimmed.slice(5).trim()
                if (data === '[DONE]') {
                  onDone()
                  return
                }

                try {
                  const parsed = JSON.parse(data)
                  const text = parsed.content || parsed.text || data
                  if (text) {
                    onChunk(text)
                  }
                } catch {
                  if (data && data !== '[DONE]') {
                    onChunk(data)
                  }
                }
              }
            }

            read()
          })
          .catch((err) => {
            if (err.name !== 'AbortError') {
              onError(err)
            }
          })
      }

      read()
    })
    .catch((err) => {
      if (err.name !== 'AbortError') {
        onError(err)
      }
    })

  return controller
}

/**
 * 结构化 SSE 流式请求 — 用于 chatEdit 等返回不同事件类型的接口
 * 每个 SSE event 的 data 是 JSON，包含 type 字段：
 *   type=thinking            → onThinking(content) — 流式思考文本
 *   type=UPDATE_BLOCK        → onAction(type, data) — 更新区块
 *   type=ADD_BLOCK           → onAction(type, data) — 新增区块
 *   type=REMOVE_BLOCK        → onAction(type, data) — 删除区块
 *   type=TOGGLE_BLOCK        → onAction(type, data) — 切换区块可见性
 *   其他 type                → onAction(type, data)
 *
 * @param {Object} params - 请求参数
 * @param {string} endpoint - API 端点
 * @param {function} onThinking - 收到 thinking 事件回调 (text: string)
 * @param {function} onAction - 收到操作事件回调 (type: string, data: object)
 * @param {function} onDone - 流结束回调
 * @param {function} onError - 错误回调 (error: Error)
 * @returns {AbortController}
 */
export function sendStructuredSSE(params, endpoint, onThinking, onAction, onDone, onError) {
  const controller = new AbortController()
  const queryString = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString()

  const url = `${endpoint}?${queryString}`

  fetch(url, {
    method: 'GET',
    credentials: 'include',
    signal: controller.signal,
    headers: {
      'Accept': 'text/event-stream',
    },
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let buffer = ''

      function read() {
        reader
          .read()
          .then(({ done, value }) => {
            if (done) {
              onDone()
              return
            }

            buffer += decoder.decode(value, { stream: true })

            const lines = buffer.split('\n')
            buffer = lines.pop() || ''

            for (const line of lines) {
              const trimmed = line.trim()
              if (!trimmed || trimmed.startsWith(':')) continue

              if (trimmed.startsWith('data:')) {
                const data = trimmed.slice(5).trim()
                if (data === '[DONE]') {
                  onDone()
                  return
                }

                try {
                  const parsed = JSON.parse(data)
                  if (parsed.type === 'thinking') {
                    const text = typeof parsed.content === 'string' ? parsed.content : JSON.stringify(parsed.content)
                    if (text) onThinking(text)
                  } else if (parsed.type) {
                    onAction(parsed.type, parsed)
                  }
                } catch {
                  // ignore malformed JSON
                }
              }
            }

            read()
          })
          .catch((err) => {
            if (err.name !== 'AbortError') {
              onError(err)
            }
          })
      }

      read()
    })
    .catch((err) => {
      if (err.name !== 'AbortError') {
        onError(err)
      }
    })

  return controller
}
